import {Injectable} from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import {JobFile} from '@core/models/job-file';
import {Observable} from 'rxjs/index';
import {API} from '@configs/api.config';
import {Pageable} from '@shared/models/pageable.model';
import {Contract} from '../../contract/models/contract.model';
import {File} from '@control-tower/models/files';
import {Invoice} from '../models/invoice.model';
import {mergeMap} from 'rxjs/operators';
import {map} from 'rxjs/operators';
import {CustomerInfo} from '@control-tower/models/customer-info.model';
import {JobFileFilterRequest} from '../models/job-file-filter-request.model';
import { EntityMap } from '@shared/models/types';
import { Jpm } from '@control-tower/models/jpm';
import { OnGoingJob } from 'app/modules/dashboard/models/on-going-job.model';
import { Vehicle } from '@shared/models/vehicle';

@Injectable({
  providedIn: 'root'
})
export class JobMasterService {

  constructor(private readonly http: HttpClient) { }

  getJobFiles(orgId: string, pageNo: number, pageSize: number): Observable<Pageable<JobFile>> {
    const url = API.jobFile.getAllJobFiles.format([orgId]);
    let params = new HttpParams();
    params = params.append('pageNo', String(pageNo));
    params = params.append('pageSize', String(pageSize));
    return this.http.get<Pageable<JobFile>>(url, {params});
  }

  filterJobFiles(jobFileFilterRequest: JobFileFilterRequest): Observable<Pageable<JobFile>> {
    const url = API.jobFile.filterJobFiles;
    return this.http.post<Pageable<JobFile>>(url, jobFileFilterRequest);
  }

  getJobFileByOrgIdAndJobRefId(orgId: string, jobRefId: string): Observable<JobFile> {
    const url = API.jobFile.getJobFileByOrgIdAndJobRefId.format([orgId, jobRefId]);
    return this.http.get<JobFile>(url);
  }

  fetchTaskFeedJobById(orgId: string, jobRefId: string) {
    const url = API.jobFile.getOngoingJobById.format([
      orgId,
      jobRefId
    ]);
    return this.http.get<OnGoingJob>(url);
  }

  getJobMasterExcel(jobFileFilterRequest: JobFileFilterRequest) {
    const url = API.report.getJobMasterExcel;
    return this.http.post(url, jobFileFilterRequest);
  }

  getCustomerInfoByCustomerId(customerId: string): Observable<CustomerInfo> {
    const url = API.fleetManager.getCustomerByCustomerId.format([customerId]);
    return this.http.get<CustomerInfo>(url);
  }

  getLatestContract(orgId: string, jobRefId: string): Observable<Contract> {
    const url = API.smartContract.getLatestContractView.format([orgId, jobRefId]);
    return this.http.get<Contract>(url);
  }

  getContractPdfInfo(orgId: string, jobRefId: string, version: number): Observable<File> {
    const url = API.smartContract.getContractPreSignedFile.format([orgId, jobRefId, version + '']);
    return this.http.get<File>(url);
  }

  getQuotationPdfInfo(orgId: string, jobRefId: string, quotationRefId: string): Observable<File> {
    const url = API.quotation.getQuotationPDFPreSignedUrlByRefId.format([orgId, jobRefId, quotationRefId]);
    return this.http.get<File>(url);
  }

  getJobSummaryPdfInfo(orgId: string, jobRefId: string): Observable<File> {
    const url = API.jobFile.getJobSummaryPdfPreSigned.format([orgId, jobRefId]);
    return this.http.get<File>(url);
  }

  getInvoice(orgId: string, jobRefId: string): Observable<Invoice> {
    const url = API.invoice.getInvoice.format([orgId, jobRefId]);
    return this.http.get<Invoice>(url);
  }

  getFileInfo(fileId: string): Observable<Blob> {
    const url = API.fileHandelar.imageUrl.format([fileId]);
    return this.http.get<File>(url).pipe(
      mergeMap((res) => this.http.get(res.url, {responseType: 'blob'})),
      map((res) => new Blob([res], {type: 'application/pdf'}))
    );
  }

  getPercentageOfContainer(orgId: string, jobRefId: string, containerIds: string[]):
   Observable<EntityMap<string, number>> {
    const url = API.jobFile.getPercentageOfContainer.format([orgId, jobRefId]);
    return this.http.post<EntityMap<string, number>>(url, { containerIds});
  }

  getJpmIdsByJobRefId(orgId: string, jobRefId: string, principalId = "" ): Observable<Jpm[]> {
    const url = API.jpm.getJpms.format([orgId, jobRefId]);
    let params = new HttpParams();
    params = params.append('principalId', principalId);
    return this.http.get<Jpm[]>(url, {params});
  }

  getTrailersOfOrgAndPartners(orgId: string, searchKey: string): Observable<Vehicle[]> {
    const url = API.organization.getTrailersOfOrgAndPartners.format([orgId]);
    const params = new HttpParams().append('searchKey', searchKey ?? '');
    return this.http.get<Vehicle[]>(url, {params});
  }
}
